import type { QueryClient } from '@tanstack/react-query';
import type { StreamEvent } from '@/api/types';

/**
 * "Something changed in this folder" for the conversation lists (ADR 0077,
 * plan/16 §8).
 *
 * A conversation list is not patched from the stream's frames: a `new_mail`
 * names messages, not the conversation they joined, and a flag frame cannot
 * say whether a conversation's head moved. What the list does instead is ask
 * the server — one `POST …/conversations/sync` per list, debounced — and the
 * list owns that request, because it holds the rows and the state token.
 *
 * So the stream only RINGS here, and the lists that are mounted listen. Keyed
 * by `QueryClient` like the rest of the realtime layer's state, so two clients
 * (two tests) never hear each other.
 */
export interface ConversationSignal {
  /** `''` means every account: the whole stream fell behind. */
  accountId: string;
  /** Absent means every folder of the account (a resync). */
  folderId?: string;
}

type Listener = (signal: ConversationSignal) => void;

const listeners = new WeakMap<QueryClient, Set<Listener>>();

export function subscribeConversationSignals(client: QueryClient, listener: Listener): () => void {
  let set = listeners.get(client);
  if (!set) {
    set = new Set();
    listeners.set(client, set);
  }
  set.add(listener);
  const owned = set;
  return () => {
    owned.delete(listener);
  };
}

export function signalConversations(client: QueryClient, signal: ConversationSignal): void {
  for (const listener of [...(listeners.get(client) ?? [])]) listener(signal);
}

/**
 * Pure: whether an event can have changed a conversation list, and which.
 *
 * `new_mail`, `flags` and `expunge` for one folder; `resync_required` for
 * every folder of its account (or of every account, when it names none). The
 * rest either cannot change a conversation (`folder_counts`: the counters
 * only) or are handled as they always were (`folder_invalidated` drops the
 * folder's lists outright).
 */
export function conversationSignalsFor(event: StreamEvent): ConversationSignal[] {
  const accountId = event.accountId ?? '';
  switch (event.kind) {
    case 'new_mail':
    case 'flags':
    case 'expunge':
      if (accountId === '' || event.folderId === undefined) return [];
      return [{ accountId, folderId: event.folderId }];
    case 'resync_required':
      return [{ accountId }];
    default:
      return [];
  }
}

/** Whether a list of (account, folder) hears a signal. */
export function signalReaches(
  signal: ConversationSignal,
  accountId: string,
  folderId: string,
): boolean {
  if (signal.accountId !== '' && signal.accountId !== accountId) return false;
  return signal.folderId === undefined || signal.folderId === folderId;
}
